// ABOUTME: The metadata-card dispatch block for an assistant chat message,
// extracted out of ChatMessage.tsx to keep that file under the
// repo's 600-line ceiling. Each card is keyed off a distinct
// `message.metadata.*` field — see aiChatTypes.ts's AIChatMessageMetadata
// for the full contract. Adding a new card type still means adding a new
// metadata key end-to-end (backend event → type → branch here).
import type { AIChatMessage } from '@/stores/aiChatTypes'
import { ContentLinkButton } from '@/components/chat/ContentLinkButton'
import { EditedImageCard } from '@/components/chat/EditedImageCard'
import { ROISummaryCard } from '@/components/chat/ROISummaryCard'
import { PeriodComparisonCard } from '@/components/chat/PeriodComparisonCard'
import { TopContentCard } from '@/components/chat/TopContentCard'
import { InsufficientCreditsCard } from '@/components/chat/InsufficientCreditsCard'
import { DraftPreview } from '@/components/chat/DraftPreview'
import { PostedConfirmation } from '@/components/chat/PostedConfirmation'
import { SchedulingCard } from '@/components/chat/SchedulingCard'
import { PlatformRequestCard } from '@/components/chat/PlatformRequestCard'
import { ProductionBriefCard } from '@/components/chat/ProductionBriefCard'
import { VariationsCard } from '@/components/chat/VariationsCard'

interface ChatMessageMetadataCardsProps {
  message: AIChatMessage
  readOnly: boolean
  onEditImage?: (imageUrl: string) => void
  onDraftEdit?: (draftId: string) => void
  onDraftSchedule?: (draftId: string, messageId: string) => void | Promise<void>
  onDraftDelete?: (draftId: string) => void | Promise<void>
  onScheduleResolve?: (messageId: string, scheduledAt: string | null) => void | Promise<void>
  onScheduleDismiss?: (messageId: string) => void
}

export function ChatMessageMetadataCards({
  message,
  readOnly,
  onEditImage,
  onDraftEdit,
  onDraftSchedule,
  onDraftDelete,
  onScheduleResolve,
  onScheduleDismiss,
}: ChatMessageMetadataCardsProps) {
  const metadata = message.metadata
  if (!metadata) return null

  return (
    <div className="mt-2 space-y-1">
      {metadata.roi_summary && <ROISummaryCard data={metadata.roi_summary} />}
      {metadata.period_comparison && <PeriodComparisonCard data={metadata.period_comparison} />}
      {metadata.top_content && <TopContentCard data={metadata.top_content} />}
      {metadata.insufficient_credits && (
        <InsufficientCreditsCard
          creditsRemaining={metadata.insufficient_credits.credits_remaining}
          creditsRequired={metadata.insufficient_credits.credits_required}
          operation={metadata.insufficient_credits.operation}
        />
      )}
      {metadata.platform_request && (
        <PlatformRequestCard
          requestId={metadata.platform_request.id}
          platform={metadata.platform_request.platform}
        />
      )}
      {metadata.production_brief && <ProductionBriefCard briefId={metadata.production_brief.id} />}
      {metadata.variation_group_id && (
        <VariationsCard groupId={metadata.variation_group_id} onTweak={(id) => onDraftEdit?.(id)} />
      )}
      {metadata.draft_preview?.map((d) => (
        <DraftPreview
          key={d.draft_id}
          draftId={d.draft_id}
          title={d.title}
          body={d.body}
          platforms={d.platforms}
          onEdit={(id) => onDraftEdit?.(id)}
          onSchedule={(id) => {
            void onDraftSchedule?.(id, message.id)
          }}
          onDelete={(id) => {
            void onDraftDelete?.(id)
          }}
        />
      ))}
      {/* Schedule/publish widget, scoped to the turn that produced it by
          construction — it renders from THIS message's metadata, so
          it can never re-appear under a later, unrelated answer the way the
          old composer-pinned card did. */}
      {metadata.scheduling_card && onScheduleResolve && onScheduleDismiss && (
        <SchedulingCard
          profiles={metadata.scheduling_card.profiles}
          noAccounts={metadata.scheduling_card.no_accounts}
          mediaUrl={metadata.scheduling_card.media_url}
          contentType={metadata.scheduling_card.content_type}
          format={metadata.scheduling_card.format ?? null}
          hasMedia={!!metadata.scheduling_card.media_url}
          onSchedule={(scheduledAt) => {
            void onScheduleResolve(message.id, scheduledAt)
          }}
          onCancel={() => onScheduleDismiss(message.id)}
        />
      )}
      {metadata.posted_confirmation && (
        <PostedConfirmation
          platform={metadata.posted_confirmation.platform}
          postUrl={metadata.posted_confirmation.post_url}
          postedAt={metadata.posted_confirmation.posted_at}
        />
      )}
      {/* Turn-closing CTA. `run_content_summary` is only emitted when the
          turn created something, so its presence is the whole condition. */}
      {metadata.run_content_summary && (
        <ContentLinkButton
          to="/content"
          label="Open Content page"
          title={`${metadata.run_content_summary.count} item${
            metadata.run_content_summary.count === 1 ? '' : 's'
          } created`}
          data-testid="open-content-page-button"
        />
      )}
      {/* An edited image renders as a compact card, never inline — viewing full-size is
          reserved for the Content page. */}
      {metadata.generated_image_asset_id && (
        <EditedImageCard
          assetId={metadata.generated_image_asset_id}
          readOnly={readOnly}
          onEditImage={onEditImage}
        />
      )}
    </div>
  )
}
